import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users, Calendar, Bell, BarChart2, Settings, LogOut,
  Plus, Trash2, Edit2, Save, X, Send, RefreshCw, Shield,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function urlBase64ToUint8Array(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem("admin_pw"));
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [logging, setLogging] = useState(false);
  const [tab, setTab] = useState("overview");

  const adminHeaders = { "x-admin-password": sessionStorage.getItem("admin_pw") || "" };

  const login = async (e) => {
    e.preventDefault();
    setLogging(true);
    setLoginErr("");
    try {
      await axios.post(`${API}/admin/verify`, { password });
      sessionStorage.setItem("admin_pw", password);
      setAuthed(true);
    } catch {
      setLoginErr("Incorrect password. Try again.");
    } finally {
      setLogging(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("admin_pw");
    setAuthed(false);
    setPassword("");
  };

  if (!authed) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: "#0F172A" }}
        data-testid="admin-login"
      >
        <div
          className="w-full max-w-sm border-2 border-black p-8"
          style={{ backgroundColor: "#1E293B", boxShadow: "8px 8px 0 #FFC82E" }}
        >
          <div className="flex items-center gap-2 mb-6">
            <Shield className="w-6 h-6" style={{ color: "#FFC82E" }} />
            <h1 className="text-xl font-black text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              AusVote Admin
            </h1>
          </div>
          <form onSubmit={login}>
            <label className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">
              Admin Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="w-full border-2 border-black px-3 py-2 text-sm font-medium focus:outline-none mb-4"
              style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
              placeholder="Enter admin password"
              autoFocus
            />
            {loginErr && (
              <p className="text-xs font-bold mb-3" style={{ color: "#C04E3F" }}>
                {loginErr}
              </p>
            )}
            <button
              type="submit"
              disabled={logging || !password}
              data-testid="admin-login-btn"
              className="w-full py-3 font-black border-2 border-black hover:-translate-y-0.5 transition-transform disabled:opacity-60"
              style={{ backgroundColor: "#FFC82E", boxShadow: "3px 3px 0 #000" }}
            >
              {logging ? "Verifying..." : "Login"}
            </button>
          </form>
          <button
            onClick={() => navigate("/")}
            className="mt-4 w-full text-center text-xs text-gray-500 hover:text-gray-400"
          >
            Back to AusVote
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#0F172A" }} data-testid="admin-dashboard">
      {/* Admin Header */}
      <div className="border-b-2 border-black px-4 py-4" style={{ backgroundColor: "#1E293B" }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5" style={{ color: "#FFC82E" }} />
            <span className="font-black text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              AusVote Admin
            </span>
            <span
              className="text-xs font-bold px-2 py-0.5 border border-black"
              style={{ backgroundColor: "#FFC82E", color: "#000" }}
            >
              Panel
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="text-xs font-bold text-gray-400 hover:text-white"
            >
              View Site
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 border-2 border-black hover:-translate-y-0.5 transition-transform"
              style={{ backgroundColor: "#C04E3F", color: "#fff" }}
            >
              <LogOut className="w-3 h-3" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 border-b-2 border-gray-700">
          {[
            { id: "overview", label: "Overview", icon: BarChart2 },
            { id: "parties", label: "Parties", icon: Users },
            { id: "elections", label: "Elections", icon: Calendar },
            { id: "push", label: "Push Notifications", icon: Bell },
            { id: "subscribers", label: "Email Subscribers", icon: Settings },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className="flex items-center gap-1.5 px-4 py-2.5 font-bold text-xs border-2 transition-all"
              style={{
                borderBottomWidth: tab === t.id ? "0" : "2px",
                borderColor: tab === t.id ? "#000" : "transparent",
                backgroundColor: tab === t.id ? "#FFC82E" : "#1E293B",
                color: tab === t.id ? "#000" : "#94A3B8",
                marginBottom: tab === t.id ? "-2px" : "0",
              }}
            >
              <t.icon className="w-3 h-3" />
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab headers={adminHeaders} />}
        {tab === "parties" && <PartiesTab headers={adminHeaders} />}
        {tab === "elections" && <ElectionsTab headers={adminHeaders} />}
        {tab === "push" && <PushTab headers={adminHeaders} />}
        {tab === "subscribers" && <SubscribersTab headers={adminHeaders} />}
      </div>
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────
function OverviewTab({ headers }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/stats`, { headers }).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cards = stats
    ? [
        { label: "Parties", value: stats.parties, color: "#CC2529", icon: Users },
        { label: "Elections", value: stats.elections, color: "#1B4F9E", icon: Calendar },
        { label: "Email Subscribers", value: stats.subscriptions, color: "#2E5948", icon: Bell },
        { label: "Push Subscribers", value: stats.push_subscribers, color: "#9333EA", icon: Bell },
        { label: "Quiz Results", value: stats.quiz_results, color: "#D97706", icon: BarChart2 },
      ]
    : [];

  return (
    <div data-testid="admin-overview">
      <h2 className="font-black text-white text-xl mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
        Dashboard Overview
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {stats ? cards.map((c) => (
          <div
            key={c.label}
            className="border-2 border-black p-4"
            style={{ backgroundColor: "#1E293B", boxShadow: "3px 3px 0 " + c.color }}
          >
            <p className="text-xs font-bold mb-1" style={{ color: "#94A3B8" }}>{c.label}</p>
            <p className="text-3xl font-black" style={{ color: c.color, fontFamily: "Outfit, sans-serif" }}>
              {c.value}
            </p>
          </div>
        )) : (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 border-2 border-black animate-pulse" style={{ backgroundColor: "#1E293B" }} />
          ))
        )}
      </div>
    </div>
  );
}

// ── Parties Tab ──────────────────────────────────────────────────
function PartiesTab({ headers }) {
  const [parties, setParties] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/parties`, { headers }).then((r) => setParties(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (p) => {
    setEditId(p.id);
    setEditData({ leader: p.leader || "", description: p.description || "", donation_url: p.donation_url || "", website: p.website || "" });
  };

  const save = async (id) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/parties/${id}`, editData, { headers });
      setMsg("Saved!");
      setEditId(null);
      load();
    } catch { setMsg("Error saving"); }
    setSaving(false);
    setTimeout(() => setMsg(""), 2000);
  };

  return (
    <div data-testid="admin-parties">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-black text-white text-xl" style={{ fontFamily: "Outfit, sans-serif" }}>Parties</h2>
        {msg && <span className="text-xs font-bold px-2 py-1 border border-black" style={{ backgroundColor: "#10B981", color: "#fff" }}>{msg}</span>}
      </div>
      <div className="space-y-2">
        {parties.map((p) => (
          <div key={p.id} className="border-2 border-black" style={{ backgroundColor: "#1E293B" }}>
            {editId === p.id ? (
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 border border-black" style={{ backgroundColor: p.color }} />
                  <span className="font-black text-white text-sm">{p.name}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { key: "leader", label: "Leader" },
                    { key: "donation_url", label: "Donation URL" },
                    { key: "website", label: "Website URL" },
                  ].map((f) => (
                    <div key={f.key}>
                      <label className="block text-xs font-bold text-gray-400 mb-1">{f.label}</label>
                      <input
                        value={editData[f.key] || ""}
                        onChange={(e) => setEditData({ ...editData, [f.key]: e.target.value })}
                        className="w-full border-2 border-black px-2 py-1.5 text-xs font-medium focus:outline-none"
                        style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
                      />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-gray-400 mb-1">Description</label>
                    <textarea
                      rows={2}
                      value={editData.description || ""}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      className="w-full border-2 border-black px-2 py-1.5 text-xs font-medium focus:outline-none resize-none"
                      style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => save(p.id)}
                    disabled={saving}
                    data-testid={`save-party-${p.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border-2 border-black"
                    style={{ backgroundColor: "#10B981", color: "#fff" }}
                  >
                    <Save className="w-3 h-3" /> Save
                  </button>
                  <button
                    onClick={() => setEditId(null)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border-2 border-black"
                    style={{ backgroundColor: "#334155", color: "#fff" }}
                  >
                    <X className="w-3 h-3" /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3">
                <div className="w-3 h-8 flex-shrink-0" style={{ backgroundColor: p.color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-black text-white text-sm">{p.name}</span>
                    <span className="text-xs font-bold text-gray-400">· {p.leader}</span>
                  </div>
                  {p.donation_url && (
                    <a href={p.donation_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs underline" style={{ color: "#FFC82E" }}>
                      {p.donation_url}
                    </a>
                  )}
                </div>
                <button
                  onClick={() => startEdit(p)}
                  data-testid={`edit-party-${p.id}`}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs font-bold border-2 border-black hover:-translate-y-0.5 transition-transform"
                  style={{ backgroundColor: "#FFC82E", color: "#000" }}
                >
                  <Edit2 className="w-3 h-3" /> Edit
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Elections Tab ─────────────────────────────────────────────────
function ElectionsTab({ headers }) {
  const [elections, setElections] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newElec, setNewElec] = useState({ name: "", type: "state", state: "", date: "", description: "", status: "upcoming" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/elections`, { headers }).then((r) => setElections(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const showMsg = (m) => { setMsg(m); setTimeout(() => setMsg(""), 2500); };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/elections/${id}`, editData, { headers });
      setEditId(null);
      load();
      showMsg("Election updated!");
    } catch { showMsg("Error saving"); }
    setSaving(false);
  };

  const deleteElec = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/elections/${id}`, { headers });
      load();
      showMsg("Deleted!");
    } catch { showMsg("Error deleting"); }
  };

  const addElec = async () => {
    if (!newElec.name || !newElec.date) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/elections`, newElec, { headers });
      setShowAdd(false);
      setNewElec({ name: "", type: "state", state: "", date: "", description: "", status: "upcoming" });
      load();
      showMsg("Election added!");
    } catch { showMsg("Error adding"); }
    setSaving(false);
  };

  const STATE_OPTS = ["federal", "NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

  return (
    <div data-testid="admin-elections">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-black text-white text-xl" style={{ fontFamily: "Outfit, sans-serif" }}>Elections</h2>
        <div className="flex items-center gap-2">
          {msg && <span className="text-xs font-bold px-2 py-1" style={{ backgroundColor: "#10B981", color: "#fff" }}>{msg}</span>}
          <button
            onClick={() => setShowAdd(!showAdd)}
            data-testid="add-election-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border-2 border-black"
            style={{ backgroundColor: "#2E5948", color: "#fff" }}
          >
            <Plus className="w-3 h-3" /> Add Election
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="border-2 border-black p-4 mb-4" style={{ backgroundColor: "#1E293B", boxShadow: "4px 4px 0 #FFC82E" }}>
          <h3 className="font-black text-white text-sm mb-3">New Election</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            {[
              { key: "name", label: "Election Name" },
              { key: "date", label: "Date (YYYY-MM-DD)" },
              { key: "description", label: "Description" },
            ].map((f) => (
              <div key={f.key} className={f.key === "description" ? "md:col-span-2" : ""}>
                <label className="block text-xs font-bold text-gray-400 mb-1">{f.label}</label>
                <input
                  value={newElec[f.key] || ""}
                  onChange={(e) => setNewElec({ ...newElec, [f.key]: e.target.value })}
                  className="w-full border-2 border-black px-2 py-1.5 text-xs font-medium focus:outline-none"
                  style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
                />
              </div>
            ))}
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Type</label>
              <select
                value={newElec.type}
                onChange={(e) => setNewElec({ ...newElec, type: e.target.value })}
                className="w-full border-2 border-black px-2 py-1.5 text-xs font-medium focus:outline-none"
                style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
              >
                <option value="federal">Federal</option>
                <option value="state">State</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">State</label>
              <select
                value={newElec.state}
                onChange={(e) => setNewElec({ ...newElec, state: e.target.value })}
                className="w-full border-2 border-black px-2 py-1.5 text-xs font-medium focus:outline-none"
                style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
              >
                <option value="">—</option>
                {STATE_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={addElec}
              disabled={saving || !newElec.name || !newElec.date}
              data-testid="save-new-election-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border-2 border-black disabled:opacity-60"
              style={{ backgroundColor: "#FFC82E" }}
            >
              <Save className="w-3 h-3" /> Save Election
            </button>
            <button
              onClick={() => setShowAdd(false)}
              className="px-3 py-1.5 text-xs font-bold border-2 border-black"
              style={{ backgroundColor: "#334155", color: "#fff" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {elections.map((e) => (
          <div key={e.id} className="border-2 border-black" style={{ backgroundColor: "#1E293B" }}>
            {editId === e.id ? (
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[{ key: "name", label: "Name" }, { key: "date", label: "Date" }, { key: "description", label: "Description" }].map((f) => (
                    <div key={f.key} className={f.key === "description" ? "md:col-span-2" : ""}>
                      <label className="block text-xs font-bold text-gray-400 mb-1">{f.label}</label>
                      <input
                        value={editData[f.key] || ""}
                        onChange={(ev) => setEditData({ ...editData, [f.key]: ev.target.value })}
                        className="w-full border-2 border-black px-2 py-1.5 text-xs font-medium focus:outline-none"
                        style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-bold text-gray-400 mb-1">Status</label>
                    <select
                      value={editData.status || "upcoming"}
                      onChange={(ev) => setEditData({ ...editData, status: ev.target.value })}
                      className="w-full border-2 border-black px-2 py-1.5 text-xs font-medium focus:outline-none"
                      style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
                    >
                      <option value="upcoming">Upcoming</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(e.id)} disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border-2 border-black"
                    style={{ backgroundColor: "#10B981", color: "#fff" }}>
                    <Save className="w-3 h-3" /> Save
                  </button>
                  <button onClick={() => setEditId(null)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border-2 border-black"
                    style={{ backgroundColor: "#334155", color: "#fff" }}>
                    <X className="w-3 h-3" /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-black text-white text-sm">{e.name}</span>
                    <span className="text-xs px-1.5 py-0.5 border border-gray-600 text-gray-400">{e.state || e.type}</span>
                    <span className="text-xs text-gray-400">{e.date}</span>
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{e.description}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => { setEditId(e.id); setEditData({ name: e.name, date: e.date, description: e.description, status: e.status }); }}
                    data-testid={`edit-election-${e.id}`}
                    className="flex items-center gap-1 px-2 py-1.5 text-xs font-bold border-2 border-black hover:-translate-y-0.5 transition-transform"
                    style={{ backgroundColor: "#FFC82E", color: "#000" }}>
                    <Edit2 className="w-3 h-3" />
                  </button>
                  <button onClick={() => deleteElec(e.id, e.name)}
                    data-testid={`delete-election-${e.id}`}
                    className="flex items-center gap-1 px-2 py-1.5 text-xs font-bold border-2 border-black hover:-translate-y-0.5 transition-transform"
                    style={{ backgroundColor: "#C04E3F", color: "#fff" }}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Push Notifications Tab ────────────────────────────────────────
function PushTab({ headers }) {
  const [subs, setSubs] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/push-subscriptions`, { headers }).then((r) => setSubs(r.data)).catch(() => {});
  }, []);

  const send = async () => {
    if (!title || !body) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/push/send`, { title, body, url }, { headers });
      setResult(r.data);
      setTitle(""); setBody(""); setUrl("/");
    } catch (e) {
      setResult({ error: e.response?.data?.detail || "Send failed" });
    }
    setSending(false);
  };

  return (
    <div data-testid="admin-push">
      <h2 className="font-black text-white text-xl mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
        Push Notifications
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Send form */}
        <div className="border-2 border-black p-5" style={{ backgroundColor: "#1E293B", boxShadow: "4px 4px 0 #FFC82E" }}>
          <h3 className="font-black text-white text-sm mb-4">Send Notification to All Subscribers</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                data-testid="push-title-input"
                className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none"
                style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
                placeholder="Election Reminder" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Body</label>
              <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)}
                data-testid="push-body-input"
                className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none resize-none"
                style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
                placeholder="SA election is in 7 days. Enrolment closes tomorrow!" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Link URL (optional)</label>
              <input value={url} onChange={(e) => setUrl(e.target.value)}
                className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none"
                style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
                placeholder="/" />
            </div>
            <button
              onClick={send}
              disabled={sending || !title || !body}
              data-testid="send-push-btn"
              className="flex items-center gap-2 px-4 py-2 font-bold text-sm border-2 border-black disabled:opacity-60 hover:-translate-y-0.5 transition-transform"
              style={{ backgroundColor: "#FFC82E", boxShadow: "2px 2px 0 #000" }}
            >
              <Send className="w-4 h-4" />
              {sending ? "Sending..." : `Send to ${subs.length} subscribers`}
            </button>
            {result && (
              <div
                className="p-3 border-2 border-black text-sm font-bold"
                style={{ backgroundColor: result.error ? "#7F1D1D" : "#064E3B", color: "#fff" }}
              >
                {result.error
                  ? `Error: ${result.error}`
                  : `Sent: ${result.sent} | Failed: ${result.failed} | Expired removed: ${result.expired_removed}`}
              </div>
            )}
          </div>
        </div>

        {/* Subscribers list */}
        <div className="border-2 border-black p-5" style={{ backgroundColor: "#1E293B" }}>
          <h3 className="font-black text-white text-sm mb-4">
            Push Subscribers ({subs.length})
          </h3>
          {subs.length === 0 ? (
            <p className="text-xs text-gray-500">No push subscribers yet. Users can enable notifications from the homepage.</p>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {subs.map((s, i) => (
                <div key={i} className="text-xs font-mono text-gray-400 truncate border-b border-gray-700 py-1">
                  {s.endpoint?.slice(0, 50)}...
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Subscribers Tab ───────────────────────────────────────────────
function SubscribersTab({ headers }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/subscriptions`, { headers })
      .then((r) => setSubs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="admin-subscribers">
      <h2 className="font-black text-white text-xl mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
        Email Subscribers ({subs.length})
      </h2>
      {loading ? (
        <div className="h-32 border-2 border-black animate-pulse" style={{ backgroundColor: "#1E293B" }} />
      ) : subs.length === 0 ? (
        <div className="border-2 border-black p-8 text-center" style={{ backgroundColor: "#1E293B" }}>
          <p className="text-gray-400 text-sm">No email subscribers yet.</p>
        </div>
      ) : (
        <div className="border-2 border-black overflow-hidden" style={{ backgroundColor: "#1E293B" }}>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b-2 border-black">
                <th className="text-left p-3 font-black text-gray-300">Email</th>
                <th className="text-left p-3 font-black text-gray-300">Elections</th>
                <th className="text-left p-3 font-black text-gray-300">Subscribed</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s, i) => (
                <tr key={i} className="border-b border-gray-700">
                  <td className="p-3 text-gray-200 font-medium">{s.email}</td>
                  <td className="p-3 text-gray-400">{(s.election_ids || []).join(", ")}</td>
                  <td className="p-3 text-gray-400">{s.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
